// TradeUnionUserDialog.js
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
} from '@mui/material';
import axios from 'axios';

const steps = [
  'Basic Information',
  'Contact Information',
  'Additional Information',
  'Communication Authority',
  'Membership Details',
];

export default function TradeUnionUserDialog({ open, onClose, userId, fetchUsers }) {
  const [activeStep, setActiveStep] = useState(0);
  
  // State for user fields aligned with the schema
  const [userData, setUserData] = useState({
    organizationName: '',
    nameofpresident: '',
    email: '',
    phoneno: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    linkedin: '',
    unionLeader: '',
    yearOfEstablishment: '',
    regdOfficeAddress: '',
    telephoneOffice: '',
    website: '',
    achievements: '',
    additionalInfo: '',
    communicationAuthority: {
      name: '',
      designation: '',
      phone: '',
      email: ''
    },
    numberOfMembers: '',
    howDidYouKnowAboutUs: '',
    membershipID: '',
    membershipCategory: '',
    tenure: '',
  });

  useEffect(() => {
    if (userId) {
      // Fetch user details for editing
      const fetchUserDetails = async () => {
        try {
          const response = await axios.get(`https://form.cpccbe.org/api/tradeunion/tradeunions/${userId}`);
          const user = response.data;
          
          // Format year of establishment
          const yearOnly = user.yearOfEstablishment 
            ? new Date(user.yearOfEstablishment).getFullYear().toString()
            : '';
            
          setUserData({
            ...user,
            yearOfEstablishment: yearOnly,
            // Ensure communicationAuthority exists
            communicationAuthority: user.communicationAuthority || {
              name: '',
              designation: '',
              phone: '',
              email: ''
            }
          });
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      };
      fetchUserDetails();
    } else {
      // Reset fields for adding a new user
      setUserData({
        organizationName: '',
        nameofpresident: '',
        email: '',
        phoneno: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        linkedin: '',
        unionLeader: '',
        yearOfEstablishment: '',
        regdOfficeAddress: '',
        telephoneOffice: '',
        website: '',
        achievements: '',
        additionalInfo: '',
        communicationAuthority: {
          name: '',
          designation: '',
          phone: '',
          email: ''
        },
        numberOfMembers: '',
        howDidYouKnowAboutUs: '',
        membershipID: '',
        membershipCategory: '',
        tenure: '',
      });
    }
  }, [userId]);

  const handleUpdateUser = async () => {
    try {
      // Prepare data for update - Convert yearOfEstablishment string to Date
      const dataToSubmit = {
        ...userData,
        yearOfEstablishment: userData.yearOfEstablishment
          ? new Date(`${userData.yearOfEstablishment}-01-01`) 
          : null
      };
      
      // Update existing user
      await axios.put(`https://form.cpccbe.org/api/tradeunion/${userId}`, dataToSubmit);
      fetchUsers(); // Refresh user list after update
      onClose(); // Close dialog after updating
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Trade Union User</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <>
            <TextField 
              label="Organization Name"
              value={userData.organizationName || ''}
              onChange={(e) => setUserData({ ...userData, organizationName: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField
              label="Name of President"
              value={userData.nameofpresident || ''}
              onChange={(e) => setUserData({ ...userData, nameofpresident: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField 
              label="Email"
              value={userData.email || ''}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField 
              label="Phone Number"
              value={userData.phoneno || ''}
              onChange={(e) => setUserData({ ...userData, phoneno: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField
              label="Address 1"
              value={userData.address1 || ''}
              onChange={(e) => setUserData({ ...userData, address1: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Address 2"
              value={userData.address2 || ''}
              onChange={(e) => setUserData({ ...userData, address2: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="City"
              value={userData.city || ''}
              onChange={(e) => setUserData({ ...userData, city: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="State"
              value={userData.state || ''}
              onChange={(e) => setUserData({ ...userData, state: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Postal Code"
              value={userData.postalCode || ''}
              onChange={(e) => setUserData({ ...userData, postalCode: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="LinkedIn"
              value={userData.linkedin || ''}
              onChange={(e) => setUserData({ ...userData, linkedin: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField 
              label="Union Leader"
              value={userData.unionLeader || ''}
              onChange={(e) => setUserData({ ...userData, unionLeader: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField 
              label="Year of Establishment"
              type="number"
              value={userData.yearOfEstablishment || ''}
              onChange={(e) => setUserData({ ...userData, yearOfEstablishment: e.target.value })}
              fullWidth 
              margin="normal"
              inputProps={{ min: 1900, max: new Date().getFullYear() }}
              placeholder="YYYY"
            />
          </>
        )}

        {activeStep === 1 && (
          <>
            <TextField 
              label="Registered Office Address"
              value={userData.regdOfficeAddress || ''}
              onChange={(e) => setUserData({ ...userData, regdOfficeAddress: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField 
              label="Telephone (Office)"
              value={userData.telephoneOffice || ''}
              onChange={(e) => setUserData({ ...userData, telephoneOffice: e.target.value })}
              fullWidth 
              margin="normal"
            />
            <TextField 
              label="Website"  
              value={userData.website || ''}  
              onChange={(e) => setUserData({ ...userData, website: e.target.value })}  
              fullWidth  
              margin="normal"  
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Number of Members</InputLabel>
              <Select 
                label="Number of Members"
                value={userData.numberOfMembers || ''}
                onChange={(e) => setUserData({ ...userData, numberOfMembers: e.target.value })}
              >
                <MenuItem value="" disabled>Select Number of Members</MenuItem>
                <MenuItem value="0-50">0-50</MenuItem>
                <MenuItem value="51-100">51-100</MenuItem>
                <MenuItem value="101-500">101-500</MenuItem>
                <MenuItem value="501-1000">501-1000</MenuItem>
                <MenuItem value="1001-5000">1001-5000</MenuItem>
                <MenuItem value="5001-10000">5001-10000</MenuItem>
                <MenuItem value="10001-50000">10001-50000</MenuItem>
                <MenuItem value="50001-100000">50001-100000</MenuItem>
                <MenuItem value="100001 and above">100001 and above</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        {activeStep === 2 && (
          <>
            <TextField 
              label="Achievements"  
              value={userData.achievements || ''}  
              onChange={(e) => setUserData({ ...userData, achievements: e.target.value })}  
              fullWidth   
              margin="normal"
              multiline
              rows={3}   
            />  
            <TextField 
              label="Additional Info"  
              value={userData.additionalInfo || ''}  
              onChange={(e) => setUserData({ ...userData, additionalInfo: e.target.value })}  
              fullWidth   
              multiline
              rows={4}
              margin="normal"
            />
          </>
        )}

        {activeStep === 3 && (
          <>
            <TextField 
              label="Communication Authority Name"   
              value={userData.communicationAuthority?.name || ''}   
              onChange={(e) => setUserData({ 
                ...userData, 
                communicationAuthority: { 
                  ...userData.communicationAuthority, 
                  name: e.target.value 
                }
              })}
              fullWidth   
              margin="normal"   
            />  
            <TextField 
              label="Communication Authority Designation"   
              value={userData.communicationAuthority?.designation || ''}   
              onChange={(e) => setUserData({ 
                ...userData, 
                communicationAuthority: { 
                  ...userData.communicationAuthority, 
                  designation: e.target.value 
                }
              })}
              fullWidth   
              margin="normal"   
            />  
            <TextField 
              label="Communication Authority Phone"   
              value={userData.communicationAuthority?.phone || ''}   
              onChange={(e) => setUserData({ 
                ...userData, 
                communicationAuthority: { 
                  ...userData.communicationAuthority, 
                  phone: e.target.value 
                }
              })}
              fullWidth   
              margin="normal"   
            />  
            <TextField 
              label="Communication Authority Email"   
              value={userData.communicationAuthority?.email || ''}   
              onChange={(e) => setUserData({ 
                ...userData, 
                communicationAuthority: { 
                  ...userData.communicationAuthority, 
                  email: e.target.value 
                }
              })}
              fullWidth   
              margin="normal"   
            />  
            <FormControl fullWidth margin="normal">
              <InputLabel>How did you know about us?</InputLabel>
              <Select
                label="How did you know about us?"
                value={userData.howDidYouKnowAboutUs || ''}
                onChange={(e) => setUserData({ ...userData, howDidYouKnowAboutUs: e.target.value })}
              >
                <MenuItem value="" disabled>How did you know about us</MenuItem>
                <MenuItem value="Google">Google</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="Twitter">Twitter</MenuItem>
                <MenuItem value="Instagram">Instagram</MenuItem>
                <MenuItem value="Referral">Referral</MenuItem>
                <MenuItem value="Others">Others</MenuItem>  
              </Select>
            </FormControl>
          </>
        )}

        {activeStep === 4 && (
          <>
            <TextField 
              label="Membership ID"  
              value={userData.membershipID || ''}  
              onChange={(e) => setUserData({ ...userData, membershipID: e.target.value })}  
              fullWidth   
              margin="normal"
            />
            <TextField 
              label="Membership Category"  
              value={userData.membershipCategory || ''}  
              onChange={(e) => setUserData({ ...userData, membershipCategory: e.target.value })}  
              fullWidth   
              margin="normal"
            />
            <TextField 
              label="Tenure"  
              value={userData.tenure || ''}  
              onChange={(e) => setUserData({ ...userData, tenure: e.target.value })}  
              fullWidth   
              margin="normal"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        
        {activeStep === steps.length - 1 ? (
          <Button onClick={handleUpdateUser}>Update User</Button>
        ) : (
          <Button onClick={handleNext}>Next</Button>
        )}
        
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}